import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import IconProvider from '@/components/ui/IconProvider';
import { Download, FileArchive, Loader2, CheckCircle } from 'lucide-react';
import { exportForUnity, downloadZip, extractLocaKeys, extractImageKeys } from '@/lib/utils/unityExport';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ImageData {
  id: string;
  name: string;
  imagekey: string;
  url: string;
  path: string;
}

interface UnityExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  html: string;
  css: string;
  images: ImageData[];
  templateName?: string;
}

export function UnityExportDialog({
  open,
  onOpenChange,
  html,
  css,
  images,
  templateName = 'UI',
}: UnityExportDialogProps) {
  const { t } = usePlatformTranslation();
  const [filename, setFilename] = useState(templateName ? `${templateName}-unity` : 'unity-ui-export');
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const { toast } = useToast();

  // Extract information for preview
  const locaKeys = extractLocaKeys(html);
  const imageKeys = extractImageKeys(html);
  const availableImages = imageKeys.filter(key => 
    images.some(img => img.imagekey === key)
  );
  const missingImages = imageKeys.filter(key => 
    !images.some(img => img.imagekey === key)
  );

  const handleExport = async () => {
    setIsExporting(true);
    setExportComplete(false);

    try {
      // Show progress toast
      toast({
        title: 'Preparing export...',
        description: 'Converting HTML/CSS to Unity format',
      });

      // Export and create zip
      const zipBlob = await exportForUnity(html, css, images, templateName);

      // Download the zip file
      const zipFilename = filename.endsWith('.zip') ? filename : `${filename}.zip`;
      downloadZip(zipBlob, zipFilename);

      setExportComplete(true);
      
      toast({
        title: 'Export successful!',
        description: `${zipFilename} has been downloaded`,
      });

      // Close dialog after a brief delay
      setTimeout(() => {
        onOpenChange(false);
        setExportComplete(false);
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isExporting) {
      onOpenChange(newOpen);
      if (!newOpen) {
        setExportComplete(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconProvider iconstring="fa-unity" size={20} className="text-foreground" />
            Export for Unity UI Toolkit
          </DialogTitle>
          <DialogDescription>
            Convert your UI to Unity's UXML/USS format with LocaKey placeholders for runtime localization
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filename Input */}
          <div className="space-y-2">
            <Label htmlFor="filename">{t('platform.ui.unityexportdialog.export_filename')}</Label>
            <Input
              id="filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder={t('platform.ui.unityexportdialog.unity_ui_export')}
              disabled={isExporting}
            />
            <p className="text-xs text-muted-foreground">
              Will be saved as {filename}.zip
            </p>
          </div>

          {/* Export Preview */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <FileArchive size={16} />
              Export Contents
            </h4>

            {/* Files List */}
            <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <IconProvider iconstring="fa-file-code" size={14} className="text-blue-500" />
                <span className="font-mono">{t('platform.ui.unityexportdialog.uiuxml')}</span>
                <span className="text-xs text-muted-foreground">(UI structure)</span>
              </div>
              <div className="flex items-center gap-2">
                <IconProvider iconstring="fa-file-code" size={14} className="text-purple-500" />
                <span className="font-mono">{t('platform.ui.unityexportdialog.uiuss')}</span>
                <span className="text-xs text-muted-foreground">(Styles)</span>
              </div>
              <div className="flex items-center gap-2">
                <IconProvider iconstring="fa-folder" size={14} className="text-yellow-500" />
                <span className="font-mono">{t('platform.ui.unityexportdialog.images')}</span>
                <span className="text-xs text-muted-foreground">
                  ({availableImages.length} {availableImages.length === 1 ? 'image' : 'images'})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <IconProvider iconstring="fa-file-lines" size={14} className="text-gray-500" />
                <span className="font-mono">{t('platform.ui.unityexportdialog.readmetxt')}</span>
                <span className="text-xs text-muted-foreground">(Integration guide)</span>
              </div>
            </div>

            {/* LocaKeys */}
            {locaKeys.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium flex items-center gap-2">
                  <IconProvider iconstring="fa-language" size={14} />
                  LocaKeys ({locaKeys.length})
                </h5>
                <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {locaKeys.map((key) => (
                      <code
                        key={key}
                        className="text-xs bg-background px-2 py-1 rounded border border-border"
                      >
                        {`{{${key}}}`}
                      </code>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  These keys will be preserved for runtime localization in Unity
                </p>
              </div>
            )}

            {/* Images */}
            {imageKeys.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium flex items-center gap-2">
                  <IconProvider iconstring="fa-image" size={14} />
                  Referenced Images ({imageKeys.length})
                </h5>
                <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto space-y-1">
                  {availableImages.map((key) => {
                    const img = images.find(i => i.imagekey === key);
                    return (
                      <div key={key} className="flex items-center gap-2 text-xs">
                        <CheckCircle size={12} className="text-green-500" />
                        <code className="bg-background px-1 py-0.5 rounded">
                          {`{{img:${key}}}`}
                        </code>
                        {img && (
                          <span className="text-muted-foreground truncate">
                            → {img.name}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {missingImages.map((key) => (
                    <div key={key} className="flex items-center gap-2 text-xs text-orange-600">
                      <IconProvider iconstring="fa-triangle-exclamation" size={12} />
                      <code className="bg-background px-1 py-0.5 rounded">
                        {`{{img:${key}}}`}
                      </code>
                      <span className="text-muted-foreground">
                        (not found - will be skipped)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warnings */}
            {(missingImages.length > 0 || locaKeys.length === 0) && (
              <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                <div className="flex gap-2">
                  <IconProvider 
                    iconstring="fa-triangle-exclamation" 
                    size={16} 
                    className="text-orange-600 dark:text-orange-500 mt-0.5" 
                  />
                  <div className="space-y-1 text-sm">
                    {missingImages.length > 0 && (
                      <p className="text-orange-800 dark:text-orange-300">
                        {missingImages.length} image{missingImages.length !== 1 ? 's' : ''} referenced 
                        but not found in library
                      </p>
                    )}
                    {locaKeys.length === 0 && (
                      <p className="text-orange-800 dark:text-orange-300">
                        No LocaKeys detected - UI will have static text
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex gap-2">
                <IconProvider 
                  iconstring="fa-circle-info" 
                  size={16} 
                  className="text-blue-600 dark:text-blue-400 mt-0.5" 
                />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-medium mb-1">{t('platform.ui.unityexportdialog.what_youll_get')}</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>{t('platform.ui.unityexportdialog.uxml_file_with_ui_structure_unitys_html_equivalent')}</li>
                    <li>{t('platform.ui.unityexportdialog.uss_file_with_styles_unitys_css_equivalent')}</li>
                    <li>{t('platform.ui.unityexportdialog.all_referenced_images_in_an_images_folder')}</li>
                    <li>{t('platform.ui.unityexportdialog.readme_with_integration_instructions_and_c_example_code')}</li>
                    <li>{t('platform.ui.unityexportdialog.locakeys_preserved_as_placeholders_for_runtime_localization')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || exportComplete}
            className="gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </>
            ) : exportComplete ? (
              <>
                <CheckCircle size={16} />
                Complete!
              </>
            ) : (
              <>
                <Download size={16} />
                Export for Unity
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

